import { useState } from 'react';
import { PARENT_RELATIONSHIP_OPTIONS } from '../../constants/parentRelationship';
import type { ParentLinkEmailAction, ParentRelationship } from '../../types';

/**
 * The one "link a parent to a child" form, shared by the admin student page
 * and the parent's child home so the two ask exactly the same two things:
 * email and relationship. No name — an existing account already has one and
 * a new account sets its own at signup.
 *
 * `onSubmit` performs the request. A rejection carrying `data.message` (the
 * backend's 409 ALREADY_LINKED) is shown inline under the email so the person
 * can correct it; other shapes go through `messageFrom` (a 400 validation
 * error has no message and reads as the generic line — the form pre-validates
 * both fields, so it should not happen). Success is the caller's to announce
 * (toast) — it knows the wording.
 */
export function LinkParentForm({
  testIdPrefix,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  testIdPrefix: string;
  submitLabel: string;
  onSubmit: (values: { email: string; relationship: ParentRelationship }) => Promise<void>;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState('');
  const [relationship, setRelationship] = useState<ParentRelationship | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Enter a valid email address.');
      return;
    }
    if (!relationship) {
      setError('Choose how this person is related to the child.');
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await onSubmit({ email: trimmed.toLowerCase(), relationship });
    } catch (err) {
      setError(messageFrom(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      data-testid={`${testIdPrefix}-form`}
      onSubmit={handleSubmit}
      className="space-y-3 border-t border-gray-100 bg-gray-50 px-4 py-3 text-sm"
      noValidate
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-gray-400">Email</span>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            autoFocus
            data-testid={`${testIdPrefix}-email`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-gray-400">
            Relationship to child
          </span>
          <select
            data-testid={`${testIdPrefix}-relationship`}
            value={relationship}
            onChange={(e) => setRelationship(e.target.value as ParentRelationship | '')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          >
            <option value="">Choose…</option>
            {PARENT_RELATIONSHIP_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error && (
        <p data-testid={`${testIdPrefix}-error`} role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          data-testid={`${testIdPrefix}-cancel`}
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          data-testid={`${testIdPrefix}-submit`}
          disabled={isSubmitting}
          className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Adding…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

const GENERIC_VALIDATION_ERRORS = new Set(['Validation error', 'Validation failed']);

/**
 * Human wording for a failed parent-link request. Prefers the backend's
 * `message` (409 ALREADY_LINKED, 409 PRIMARY_PARENT, 404 NOT_LINKED …), then
 * a specific line for the shapes this feature's handlers actually return,
 * and only then the generic fallback. Always logs the raw error so a
 * "something went wrong" is never silent in the console.
 */
export function messageFrom(err: unknown): string {
  console.error('Parent link request failed', err);
  const e = err as { status?: unknown; data?: { message?: string; error?: string } } | undefined;
  if (e?.data?.message) return e.data.message;
  if (e?.status === 403) return "You no longer have permission to change this child's parents.";
  if (e?.status === 'FETCH_ERROR') {
    return 'Could not reach the server. Check your connection and try again.';
  }
  if (typeof e?.data?.error === 'string' && !GENERIC_VALIDATION_ERRORS.has(e.data.error)) {
    return e.data.error;
  }
  return 'Something went wrong. Please try again.';
}

export type LinkOutcome = { tone: 'success' | 'error'; message: string };

/**
 * The toast for a link request that returned 2xx, keyed on what the backend
 * did on the email side. Shared so the admin page and the parent page tell
 * the same story. 'email-failed' is a 2xx (the link IS in place) but reads
 * as an error: the person will not receive anything unless someone acts.
 */
export function describeLinkOutcome(
  result: { email: string; name: string; emailAction: ParentLinkEmailAction },
  childName: string
): LinkOutcome {
  switch (result.emailAction) {
    case 'signup-invite':
      return {
        tone: 'success',
        message: `Invite sent to ${result.email}. They have 14 days to create their account.`,
      };
    case 'notification':
      return {
        tone: 'success',
        message: `Linked ${
          result.name || result.email
        }. They'll see ${childName} the next time they sign in.`,
      };
    case 'skipped':
      return {
        tone: 'success',
        message: `${result.email} already has an invite waiting for ${childName}.`,
      };
    case 'email-failed':
      return {
        tone: 'error',
        message: `${result.email} is linked to ${childName}, but the email could not be sent. Ask the service to resend the invite.`,
      };
  }
}
